import React from 'react';
import { Helmet } from 'react-helmet-async';

// Canonical origin. Hardcoded (not window.location) so values baked during
// react-snap prerendering point at production, not the localhost prerender host.
export const SITE_URL = 'https://pandobit.com';
const SITE_NAME = 'Pandobit';
const DEFAULT_IMAGE = `${SITE_URL}/img/og-default.jpg`;

/**
 * Per-page SEO tags (title, description, canonical, Open Graph, Twitter, JSON-LD).
 *
 * @param {string} title        Full <title> text.
 * @param {string} description  Meta + OG/Twitter description.
 * @param {string} path         Route path beginning with "/" (e.g. "/services/cybersecurity").
 * @param {string} [image]      Absolute OG image URL. Falls back to the default share image.
 * @param {string} [type]       OG type ("website" | "article"). Default "website".
 * @param {object} [jsonLd]     Structured data object serialized into a JSON-LD script.
 * @param {boolean} [noindex]   Emit a robots noindex tag (e.g. not-found pages).
 */
export const Seo = ({ title, description, path = '/', image, type = 'website', jsonLd, noindex = false }) => {
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  return (
    <Helmet prioritizeSeoTags defer={false}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};
